<p-toast />
<p-confirmDialog key="notifications-page" [draggable]="false" />
<div>
  <app-page-breadcrumb pageTitle="Notifications" [breadcrumbs]="breadcrumbs" />
  <div
    class="min-h-screen rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]"
  >
    <div class="w-full max-w-screen-2xl mx-auto">
      <div
        class="flex flex-col px-6 py-4 gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex items-center gap-2.5">
          <h2 class="text-xl font-semibold text-gray-800 dark:text-white/90">
            Notifications
          </h2>
          <span
            class="text-[11px] font-bold px-2 py-0.5 rounded-full"
            [ngClass]="{
              'text-white bg-brand-500': unreadCount > 0,
              'text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800':
                unreadCount === 0,
            }"
          >
            {{ unreadCount }} new
          </span>
        </div>
        <div class="flex items-center gap-2">
          <button
            (click)="markAllRead()"
            [disabled]="unreadCount === 0 || markingAllRead"
            class="text-[11px] font-medium px-2.5 py-1 rounded-md border border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            Mark all read
          </button>
          <button
            (click)="deleteAllNotifications()"
            [disabled]="total === 0"
            class="text-[11px] font-medium px-2.5 py-1 rounded-md border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            Delete All
          </button>
        </div>
      </div>

      <div
        *ngIf="loading"
        class="divide-y divide-gray-100 dark:divide-gray-800"
        aria-hidden="true"
      >
        @for (i of [1, 2, 3, 4]; track i) {
          <div class="flex items-start gap-2.5 px-6 py-2.5">
            <span
              class="notification-skeleton mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full"
            ></span>
            <div class="flex-1 min-w-0 space-y-2">
              <div class="notification-skeleton h-3 w-3/4 rounded"></div>
              <div class="notification-skeleton h-3 w-1/2 rounded"></div>
              <div class="notification-skeleton h-2.5 w-24 rounded"></div>
            </div>
          </div>
        }
      </div>

      <div
        *ngIf="!loading"
        class="divide-y divide-gray-100 dark:divide-gray-800"
      >
        <ng-container *ngIf="notifications.length; else emptyState">
          <div
            *ngFor="let item of notifications"
            (click)="onRowClick(item)"
            class="group flex items-start gap-2.5 px-6 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/5"
            [ngClass]="{
              'bg-brand-50/50 dark:bg-brand-500/[0.06]': !item.is_read,
              'cursor-pointer': canNavigate(item),
            }"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              [ngClass]="{
                'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400':
                  !item.is_read,
                'bg-gray-100 text-gray-400 dark:bg-white/5 dark:text-gray-500':
                  item.is_read,
              }"
              aria-hidden="true"
            >
              <i
                class="pi text-sm"
                [ngClass]="getModuleIcon(item.module_id)"
              ></i>
            </span>
            <div class="flex-1 min-w-0">
              <p
                class="text-[13px] leading-snug break-words"
                [ngClass]="{
                  'font-semibold text-gray-900 dark:text-white': !item.is_read,
                  'font-normal text-gray-500 dark:text-gray-400': item.is_read,
                }"
              >
                {{ item.message }}
              </p>
              <span
                *ngIf="item.reference_id === 0 || item.reference_id === '0'"
                class="inline-block text-[10px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded mb-1"
              >
                {{ getModuleName(item.module_id) }}
              </span>
              <div class="flex items-center gap-1 mt-1">
                <div
                  class="flex items-center gap-1"
                  [title]="formatNotificationDate(item.created_on)"
                >
                  <i
                    class="pi pi-clock text-[10px] text-gray-400 dark:text-gray-500"
                  ></i>
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">
                    {{ getTimeAgo(item.created_on) }}
                  </span>
                </div>
              </div>
            </div>
            <button
              (click)="deleteNotification(item, $event)"
              class="shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 dark:text-gray-500 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 focus:opacity-100"
              pTooltip="Delete"
              tooltipPosition="top"
              aria-label="Delete notification"
            >
              <i class="pi pi-trash text-[11px]"></i>
            </button>
          </div>
        </ng-container>
        <ng-template #emptyState>
          <div
            class="flex flex-col items-center justify-center px-6 py-16 text-center"
          >
            <span
              class="flex items-center justify-center w-14 h-14 rounded-full bg-brand-50 dark:bg-brand-500/10"
            >
              <i class="pi pi-bell text-2xl text-brand-500"></i>
            </span>
            <p class="text-sm font-semibold text-gray-800 dark:text-white mt-4">
              You're all caught up
            </p>
            <p class="text-[13px] text-gray-400 dark:text-gray-500 mt-1">
              New updates from your team will appear here.
            </p>
          </div>
        </ng-template>
      </div>

      <div
        *ngIf="!loading && totalPages > 1"
        class="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-800"
      >
        <div class="text-sm text-gray-600 dark:text-gray-400">
          {{ getStartRecord() }} - {{ getEndRecord() }} of {{ total }} Records
        </div>
        <ul class="hidden items-center gap-0.5 sm:flex">
          <li>
            <button
              (click)="goToPage(page - 1)"
              [disabled]="page === 1"
              aria-label="Previous page"
              class="flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium text-gray-700 hover:bg-brand-500/[0.08] hover:text-brand-500 dark:text-gray-400 dark:hover:bg-brand-500 dark:hover:text-white disabled:opacity-40 disabled:pointer-events-none"
            >
              <i class="pi pi-angle-left"></i>
            </button>
          </li>
          @for (p of getVisiblePages(); track p) {
            <li>
              <button
                (click)="goToPage(p)"
                class="flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium"
                [ngClass]="{
                  'bg-brand-500 text-white': page === p,
                  'text-gray-700 hover:bg-brand-500/[0.08] dark:hover:bg-brand-500 dark:hover:text-white hover:text-brand-500 dark:text-gray-400':
                    page !== p,
                }"
              >
                {{ p }}
              </button>
            </li>
          }
          <li>
            <button
              (click)="goToPage(page + 1)"
              [disabled]="page === totalPages"
              aria-label="Next page"
              class="flex h-10 w-10 items-center justify-center rounded-lg text-theme-sm font-medium text-gray-700 hover:bg-brand-500/[0.08] hover:text-brand-500 dark:text-gray-400 dark:hover:bg-brand-500 dark:hover:text-white disabled:opacity-40 disabled:pointer-events-none"
            >
              <i class="pi pi-angle-right"></i>
            </button>
          </li>
        </ul>
        <span
          class="block text-sm font-medium text-gray-700 dark:text-gray-400 sm:hidden"
        >
          Page {{ page }} of {{ totalPages }}
        </span>
      </div>
    </div>
  </div>
</div>

@if (notifyLoading) {
  <div
    class="dash-loader-fade fixed inset-0 z-50 flex items-center justify-center bg-white/60 dark:bg-slate-950/60 backdrop-blur-sm"
  >
    <div class="dash-loader-fade flex flex-col items-center gap-4">
      <div class="relative">
        <div class="dash-loader-glow"></div>
        <div class="dash-loader-ring"></div>
      </div>
      <span class="dash-loader-text text-sm font-semibold tracking-wide">
        Loading All Notifications…
      </span>
      <div class="dash-loader-bar"></div>
    </div>
  </div>
}
